import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useParams, useNavigate, Link, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { applySeo, injectTracking } from "../lib/seo";
import {
  Search, X, Heart, ShoppingBag, Phone, Mail, MapPin,
  MessageCircle, Loader2, ChevronDown, Palette, Ruler,
  AlertCircle, CheckCircle, Star, Play, ChevronLeft,
  ChevronRight, Quote, User, Plus, ShoppingCart, Filter,
  Lock, Eye, EyeOff
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger,
} from "../components/ui/sheet";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";

// Sub-components
import { DS, FONTS_CSS, getImageSrc } from "../components/vitrine/VitrineShared";
import { ShirtPlaceholder, LuxeSkeletonCard, LuxeProductCard } from "../components/vitrine/VitrineProductCard";
import { InlineAuthModal } from "../components/vitrine/VitrineAuthModal";
import { ReviewsSection } from "../components/vitrine/VitrineReviews";

const API = process.env.REACT_APP_BACKEND_URL;

// ─── Main Component ───────────────────────────────────────────────────────────
export default function PublicVitrine() {
  const { userId } = useParams();
  const navigate = useNavigate();

  // ── Existing state (unchanged) ────────────────────────────────────────────
  const [loading, setLoading]           = useState(true);
  const [boutiqueName, setBoutiqueName]       = useState("");
  const [products, setProducts]         = useState([]);
  const [categories, setCategories]     = useState([]);
  const [filters, setFilters]           = useState({ sizes:[], colors:[] });
  const [accentColor, setAccentColor]   = useState("#9C7B4E");
  const [devise, setDevise]             = useState("€");
  const [contactInfo, setContactInfo]   = useState(null);
  const [reviews, setReviews]           = useState([]);
  const [averageRating, setAverageRating] = useState(0);
  const [productRatings, setProductRatings] = useState({});
  const [customerToken, setCustomerToken]   = useState(null);
  const [customerFavorites, setCustomerFavorites] = useState(new Set());
  const [favoritesCount, setFavoritesCount]       = useState(0);
  const [searchQuery, setSearchQuery]   = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedSize, setSelectedSize]     = useState("");
  const [selectedColor, setSelectedColor]   = useState("");
  const [priceRange, setPriceRange]         = useState({ min:"", max:"" });
  const [showFilters, setShowFilters]       = useState(false);
  const [selectedProduct, setSelectedProduct]   = useState(null);
  const [productDetailOpen, setProductDetailOpen] = useState(false);
  // [SEO] Métadonnées vitrine renvoyées par l'API
  const [seoMeta, setSeoMeta] = useState(null);
  const location = useLocation();
  const [selectedVariant, setSelectedVariant]     = useState({ size:"", color:"" });
  const [interestFormOpen, setInterestFormOpen]   = useState(false);
  const [interestForm, setInterestForm]   = useState({ customer_name:"", customer_phone:"", customer_email:"", message:"", quantity:1 });
  const [submitting, setSubmitting]       = useState(false);
  const [orderSuccess, setOrderSuccess]   = useState(null);

  // ── New UI state ──────────────────────────────────────────────────────────
  const [sortOrder, setSortOrder]   = useState("default");
  const [cartCount, setCartCount]   = useState(0);
  const [clienteSession, setClienteSession] = useState(null); // { name, phone }
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [pendingCartProduct, setPendingCartProduct] = useState(null); // { product, size, color, variant_id, quantity }
  // [CONV-1-SYSTEME] Pending interest after auth
  const [pendingInterest, setPendingInterest] = useState(null);
  const [interestLoading, setInterestLoading] = useState(false);

  // ── Data fetching (unchanged) ─────────────────────────────────────────────
  const fetchVitrine = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (searchQuery)      params.append("search",    searchQuery);
      if (selectedCategory) params.append("category",  selectedCategory);
      if (selectedSize)     params.append("size",      selectedSize);
      if (selectedColor)    params.append("color",     selectedColor);
      if (priceRange.min)   params.append("min_price", priceRange.min);
      if (priceRange.max)   params.append("max_price", priceRange.max);

      const [vitrineRes, reviewsRes] = await Promise.all([
        axios.get(`${API}/api/public/vitrine/${userId}?${params.toString()}`),
        axios.get(`${API}/api/public/reviews/${userId}`)
      ]);

      setBoutiqueName(vitrineRes.data.boutique_name || vitrineRes.data.store_name || "");
      setProducts(vitrineRes.data.products);
      setCategories(vitrineRes.data.categories);
      setFilters(vitrineRes.data.filters);
      if (vitrineRes.data.accent_color) setAccentColor(vitrineRes.data.accent_color);
      if (vitrineRes.data.devise)       setDevise(vitrineRes.data.devise);
      if (vitrineRes.data.contact)      setContactInfo(vitrineRes.data.contact);
      if (vitrineRes.data.seo)          setSeoMeta({ ...vitrineRes.data.seo, boutique: vitrineRes.data.boutique });

      setReviews(reviewsRes.data.reviews || []);
      setAverageRating(reviewsRes.data.average_rating || 0);

      const ratingsMap = {};
      for (const review of reviewsRes.data.reviews || []) {
        for (const productId of review.product_ids || []) {
          if (!ratingsMap[productId]) ratingsMap[productId] = { total:0, count:0 };
          ratingsMap[productId].total += review.rating;
          ratingsMap[productId].count += 1;
        }
      }
      Object.keys(ratingsMap).forEach(id => {
        ratingsMap[id].rating = Math.round((ratingsMap[id].total / ratingsMap[id].count) * 10) / 10;
      });
      setProductRatings(ratingsMap);
    } catch (error) {
      if (error.response?.status === 404) toast.error("Boutique non trouvée");
    } finally {
      setLoading(false);
    }
  }, [userId, searchQuery, selectedCategory, selectedSize, selectedColor, priceRange]);

  useEffect(() => {
    const token = localStorage.getItem(`customer_token_${userId}`);
    if (token) { setCustomerToken(token); fetchCustomerFavorites(token); fetchCartCount(token); }
    // Check cliente session from onboarding
    const savedName = sessionStorage.getItem(`cliente_name_${userId}`);
    const savedPhone = sessionStorage.getItem(`cliente_phone_${userId}`);
    if (savedName) {
      setClienteSession({ name: savedName, phone: savedPhone || "" });
      setInterestForm(prev => ({
        ...prev,
        customer_name: savedName,
        customer_phone: savedPhone || prev.customer_phone,
      }));
    }
  }, [userId]);

  const fetchCustomerFavorites = async (token) => {
    try {
      const response = await axios.get(`${API}/api/customer/favorites`, { headers:{ Authorization:`Bearer ${token}` } });
      const favSet = new Set(response.data.map(f => f.product_id));
      setCustomerFavorites(favSet);
      setFavoritesCount(favSet.size);
    } catch (error) {
      if (error.response?.status === 401) { localStorage.removeItem(`customer_token_${userId}`); setCustomerToken(null); }
    }
  };

  const toggleFavorite = async (productId) => {
    if (!customerToken) { navigate(`/vitrine/${userId}/login`); return; }
    try {
      if (customerFavorites.has(productId)) {
        await axios.delete(`${API}/api/customer/favorites/${productId}`, { headers:{ Authorization:`Bearer ${customerToken}` } });
        setCustomerFavorites(prev => { const s = new Set(prev); s.delete(productId); return s; });
        setFavoritesCount(p => p - 1);
        toast.success("Retiré des favoris");
      } else {
        await axios.post(`${API}/api/customer/favorites`, { product_id:productId }, { headers:{ Authorization:`Bearer ${customerToken}` } });
        setCustomerFavorites(prev => { const s = new Set(prev); s.add(productId); return s; });
        setFavoritesCount(p => p + 1);
        toast.success("Ajouté aux favoris");
      }
    } catch { toast.error("Erreur lors de la mise à jour des favoris"); }
  };

  // ── Cart functions ──────────────────────────────────────────────────────
  const fetchCartCount = async (token) => {
    try {
      const res = await axios.get(`${API}/api/customer/cart`, { headers: { Authorization: `Bearer ${token}` } });
      setCartCount(res.data.items?.reduce((sum, i) => sum + i.quantity, 0) || 0);
    } catch { /* silent */ }
  };

  const addToCart = async (token, product, size, color, variantId, qty = 1) => {
    try {
      const imgSrc = getImageSrc(product);
      await axios.post(`${API}/api/customer/cart`, {
        product_id: product.id,
        product_name: product.name,
        variant_id: variantId || null,
        size: size || null,
        color: color || null,
        quantity: qty,
        unit_price: product.sale_price,
        image: imgSrc?.startsWith('http') ? imgSrc : null,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`${product.name} ajouté au panier`);
      fetchCartCount(token);
    } catch {
      toast.error("Erreur lors de l'ajout au panier");
    }
  };

  const handleCommander = () => {
    if (!selectedVariant.size || !selectedVariant.color) {
      toast.error("Veuillez sélectionner une taille et une couleur");
      return;
    }
    const variantId = getSelectedVariantId();
    const pending = {
      product: selectedProduct,
      size: selectedVariant.size,
      color: selectedVariant.color,
      variant_id: variantId,
      quantity: interestForm.quantity || 1,
    };

    if (customerToken) {
      addToCart(customerToken, pending.product, pending.size, pending.color, pending.variant_id, pending.quantity);
      setProductDetailOpen(false);
    } else {
      setPendingCartProduct(pending);
      setAuthModalOpen(true);
    }
  };

  const handleAuthSuccess = (token) => {
    setCustomerToken(token);
    setAuthModalOpen(false);
    fetchCustomerFavorites(token);
    if (pendingCartProduct) {
      const p = pendingCartProduct;
      addToCart(token, p.product, p.size, p.color, p.variant_id, p.quantity);
      setPendingCartProduct(null);
      setProductDetailOpen(false);
    }
    // [CONV-1-SYSTEME] Si interest en attente après auth
    if (pendingInterest) {
      sendInterest(token, pendingInterest);
      setPendingInterest(null);
    }
  };

  // [CONV-1-SYSTEME] Envoyer "Je suis intéressée"
  const handleInteresse = () => {
    if (!selectedProduct) return;
    const data = {
      produitId: selectedProduct.id,
      produitNom: selectedProduct.name,
      produitImage: getImageSrc(selectedProduct),
      taille: selectedVariant.size || null,
      couleur: selectedVariant.color || null,
      prix: selectedProduct.sale_price,
      devise: devise
    };

    if (customerToken) {
      sendInterest(customerToken, data);
    } else {
      setPendingInterest(data);
      setAuthModalOpen(true);
    }
  };

  const sendInterest = async (token, data) => {
    setInterestLoading(true);
    try {
      const res = await axios.post(`${API}/api/conversations/${userId}/init`, data,
        { headers: { Authorization: `Bearer ${token}` } });
      if (res.data.data?.isNew) {
        toast.success("Conversation créée !");
      } else {
        toast.success("Ajouté à votre conversation !");
      }
      setProductDetailOpen(false);
      // Redirect to customer messages after 1.5s
      setTimeout(() => navigate(`/vitrine/${userId}/espace/messages`), 1500);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'envoi");
    } finally {
      setInterestLoading(false);
    }
  };

  useEffect(() => { if (userId) fetchVitrine(); }, [userId, fetchVitrine]);

  // [SEO] Balises dynamiques + JSON-LD Store / Product, tracking par boutique
  useEffect(() => {
    if (!seoMeta) return;
    const base = `${window.location.origin}/vitrine/${userId}`;
    const img = seoMeta.image && String(seoMeta.image).startsWith("http") ? seoMeta.image : undefined;
    if (productDetailOpen && selectedProduct) {
      const price = selectedProduct.sale_price;
      const inStock = (selectedProduct.variants || []).some(v => v.quantity > 0) || !selectedProduct.is_out_of_stock;
      const pImg = selectedProduct.image && String(selectedProduct.image).startsWith("http") ? selectedProduct.image : img;
      applySeo({
        title: `${selectedProduct.name} — ${boutiqueName}`,
        description: `${selectedProduct.name} à ${price} ${devise} chez ${boutiqueName}${seoMeta.city ? ` (${seoMeta.city})` : ""}. Commandez sur WhatsApp.`,
        keywords: [selectedProduct.name, selectedProduct.category_name, ...(seoMeta.keywords || [])].filter(Boolean),
        canonical: `${base}?produit=${selectedProduct.id}`,
        image: pImg, type: "product", jsonLdId: "vitrine",
        jsonLd: {
          "@context": "https://schema.org", "@type": "Product",
          name: selectedProduct.name, description: selectedProduct.material || undefined, image: pImg,
          category: selectedProduct.category_name, brand: { "@type": "Brand", name: boutiqueName },
          offers: { "@type": "Offer", price, priceCurrency: seoMeta.boutique?.currency || "EUR",
            availability: inStock ? "https://schema.org/InStock" : "https://schema.org/OutOfStock", url: `${base}?produit=${selectedProduct.id}` },
        },
      });
    } else {
      const sameAs = Object.values(seoMeta.socials || {}).filter(Boolean);
      applySeo({
        title: seoMeta.title, description: seoMeta.description, keywords: seoMeta.keywords,
        canonical: base, image: img, jsonLdId: "vitrine",
        jsonLd: {
          "@context": "https://schema.org", "@type": "Store", name: boutiqueName, description: seoMeta.description, url: base,
          image: img, telephone: contactInfo?.telephone || seoMeta.boutique?.whatsapp || undefined,
          address: seoMeta.city ? { "@type": "PostalAddress", addressLocality: seoMeta.city, streetAddress: contactInfo?.adresse || undefined } : undefined,
          sameAs: sameAs.length ? sameAs : undefined,
          hasOfferCatalog: products.length ? { "@type": "OfferCatalog", name: "Articles", numberOfItems: products.length,
            itemListElement: products.slice(0, 20).map((p, i) => ({ "@type": "Offer", position: i + 1, itemOffered: { "@type": "Product", name: p.name, image: p.image && String(p.image).startsWith("http") ? p.image : undefined }, price: p.sale_price, priceCurrency: seoMeta.boutique?.currency || "EUR", availability: p.is_out_of_stock ? "https://schema.org/OutOfStock" : "https://schema.org/InStock" })) } : undefined,
        },
      });
    }
    injectTracking(seoMeta.tracking || {});
  }, [seoMeta, productDetailOpen, selectedProduct, boutiqueName, products, contactInfo, devise, userId]);

  // [SEO] Ouverture directe d'un article via ?produit=<id> (liens de partage)
  useEffect(() => {
    const pid = new URLSearchParams(location.search).get("produit");
    if (!pid || loading || productDetailOpen) return;
    const p = products.find(x => x.id === pid);
    if (p) openProductDetail(p);
  }, [location.search, loading, products]); // eslint-disable-line react-hooks/exhaustive-deps

  const openProductDetail = async (product) => {
    try {
      const response = await axios.get(`${API}/api/public/vitrine/${userId}/product/${product.id}`);
      setSelectedProduct(response.data.product);
      setSelectedVariant({ size:"", color:"" });
      setProductDetailOpen(true);
    } catch { toast.error("Erreur lors du chargement de l'article"); }
  };

  const openInterestForm = () => {
    if (!selectedVariant.size || !selectedVariant.color) { toast.error("Veuillez sélectionner une taille et une couleur"); return; }
    setInterestFormOpen(true);
  };

  const submitInterest = async (e) => {
    e.preventDefault();
    if (!interestForm.customer_name || !interestForm.customer_phone) { toast.error("Veuillez remplir les champs obligatoires"); return; }
    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/api/public/vitrine/${userId}/orders`, {
        items:[{ product_id:selectedProduct.id, product_name:selectedProduct.name,
          variant_id:getSelectedVariantId(), size:selectedVariant.size, color:selectedVariant.color,
          quantity:interestForm.quantity || 1, unit_price:selectedProduct.sale_price }],
        customer_name:interestForm.customer_name, customer_phone:interestForm.customer_phone,
        customer_email:interestForm.customer_email || null, customer_message:interestForm.message || null
      });
      setOrderSuccess({ orderNumber:response.data.order_number, accessUrl:response.data.access_url });
      setCartCount(p => p + (interestForm.quantity || 1));
      setInterestForm({ customer_name:"", customer_phone:"", customer_email:"", message:"", quantity:1 });
    } catch { toast.error("Erreur lors de l'envoi de votre commande"); }
    finally { setSubmitting(false); }
  };

  const getSelectedVariantId = () => {
    if (!selectedProduct) return null;
    return selectedProduct.variants.find(v => v.size === selectedVariant.size && v.color === selectedVariant.color)?.id;
  };

  const getSelectedVariantStock = () => {
    if (!selectedProduct || !selectedVariant.size || !selectedVariant.color) return null;
    return selectedProduct.variants.find(v => v.size === selectedVariant.size && v.color === selectedVariant.color)?.quantity || 0;
  };

  const clearFilters = () => {
    setSearchQuery(""); setSelectedCategory(""); setSelectedSize(""); setSelectedColor(""); setPriceRange({ min:"", max:"" });
  };

  const hasActiveFilters = searchQuery || selectedCategory || selectedSize || selectedColor || priceRange.min || priceRange.max;

  // ── Client-side sort ──────────────────────────────────────────────────────
  const sortedProducts = useMemo(() => {
    const arr = [...products];
    if (sortOrder === 'price_asc')  arr.sort((a,b) => a.sale_price - b.sale_price);
    if (sortOrder === 'price_desc') arr.sort((a,b) => b.sale_price - a.sale_price);
    if (sortOrder === 'name')       arr.sort((a,b) => a.name.localeCompare(b.name));
    return arr;
  }, [products, sortOrder]);

  // ─── Loading state ─────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="vr">
        <style>{FONTS_CSS}</style>
        <nav style={{ background:DS.cream, borderBottom:'1px solid rgba(26,23,20,0.08)', padding:'1rem 2rem' }}>
          <div style={{ maxWidth:'1200px', margin:'0 auto', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
            <div style={{ height:'22px', width:'150px', background:DS.sand, borderRadius:'4px' }} />
            <div style={{ display:'flex', gap:'12px' }}>
              <div style={{ height:'36px', width:'90px', background:DS.sand, borderRadius:'24px' }} />
              <div style={{ height:'36px', width:'130px', background:DS.border, borderRadius:'24px' }} />
            </div>
          </div>
        </nav>
        <div style={{ background:DS.ink, padding:'40px 32px', minHeight:'200px' }} />
        <main style={{ maxWidth:'1200px', margin:'2.5rem auto', padding:'0 2rem' }}>
          <div className="vr-grid" style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:'14px' }}>
            {[...Array(8)].map((_,i) => <LuxeSkeletonCard key={i} />)}
          </div>
        </main>
      </div>
    );
  }

  // ─── Main render ───────────────────────────────────────────────────────────
  return (
    <div className="vr">
      <style>{FONTS_CSS}</style>

      {/* 1. NAV BAR ──────────────────────────────────────────────────────── */}
      <header style={{ background:DS.cream, borderBottom:'1px solid rgba(26,23,20,0.08)', position:'sticky', top:0, zIndex:100 }}>
        <div className="vr-nav-inner" style={{ maxWidth:'1200px', margin:'0 auto', padding:'1rem 2rem', display:'flex', alignItems:'center', justifyContent:'space-between', gap:'1rem' }}>
          {/* Brand + tagline */}
          <div>
            <h1 className="vr-serif" style={{ fontSize:'20px', fontWeight:600, color:DS.ink, lineHeight:1.2 }}>
              {boutiqueName}
            </h1>
            <p style={{ fontFamily:'DM Sans,sans-serif', fontSize:'10px', color:DS.gold, letterSpacing:'0.18em', textTransform:'uppercase', marginTop:'2px' }}>
              Mode & Accessoires
            </p>
          </div>

          {/* Nav buttons */}
          <div className="vr-nav-btns" style={{ display:'flex', alignItems:'center', gap:'10px', flexShrink:0 }}>
            {/* Connexion pill – outlined */}
            <Link
              to={customerToken ? `/vitrine/${userId}/espace/favorites` : `/vitrine/${userId}/login`}
              style={{
                display:'inline-flex', alignItems:'center', gap:'6px',
                padding:'8px 18px', borderRadius:'24px',
                border:`1.5px solid rgba(26,23,20,0.15)`, color:DS.ink, textDecoration:'none',
                fontFamily:'DM Sans,sans-serif', fontSize:'13px', fontWeight:400,
                transition:'border-color 0.2s', background:'transparent',
              }}
              data-testid="customer-space-btn"
            >
              <User style={{ width:'14px', height:'14px' }} />
              <span>{customerToken ? "Mon espace" : "Connexion"}</span>
            </Link>

            {/* Panier masqué temporairement
            <button
              style={{
                display:'inline-flex', alignItems:'center', gap:'6px', position:'relative',
                padding:'8px 18px', borderRadius:'24px',
                background:DS.ink, color:DS.white, border:'none', cursor:'pointer',
                fontFamily:'DM Sans,sans-serif', fontSize:'13px', fontWeight:400,
              }}
              data-testid="cart-btn"
              onClick={() => {
                if (customerToken) {
                  navigate(`/vitrine/${userId}/espace/cart`);
                } else {
                  setPendingCartProduct(null);
                  setAuthModalOpen(true);
                }
              }}
            >
              <ShoppingCart style={{ width:'14px', height:'14px' }} />
              <span>Mon panier</span>
              {cartCount > 0 && (
                <span style={{
                  position:'absolute', top:'-4px', right:'-4px',
                  background:'#FF6B35', color:'#fff', fontSize:'10px', fontWeight:600,
                  width:'18px', height:'18px', borderRadius:'50%',
                  display:'flex', alignItems:'center', justifyContent:'center',
                }}>
                  {cartCount}
                </span>
              )}
            </button>
            */}
          </div>
        </div>
      </header>

      {/* 2. HERO BANNER ──────────────────────────────────────────────────── */}
      <section className="vr-hero" style={{
        background:DS.ink, padding:'40px 32px', display:'flex', alignItems:'center',
        justifyContent:'space-between', gap:'2rem', maxWidth:'100%',
      }}>
        <div style={{ maxWidth:'1200px', margin:'0 auto', width:'100%', display:'flex', alignItems:'center', justifyContent:'space-between', gap:'2rem' }}>
          {/* Left */}
          <div style={{ flex:1 }}>
            <p style={{ fontFamily:'DM Sans,sans-serif', fontSize:'10px', color:DS.gold,
              letterSpacing:'4px', textTransform:'uppercase', marginBottom:'1rem' }}>
              Collection Printemps 2026
            </p>
            <h2 className="vr-hero-h1 vr-serif" style={{
              fontSize:'48px', fontWeight:300, color:DS.white, lineHeight:1.15, marginBottom:'0.75rem',
              maxWidth:'500px',
            }}>
              La mode, à<br />
              <em style={{ fontStyle:'italic', color:DS.goldLight, fontWeight:300 }}>votre image</em>
            </h2>
            <p style={{ fontFamily:'DM Sans,sans-serif', fontSize:'13px', color:'#7A736B', marginBottom:'1.5rem', maxWidth:'400px', lineHeight:1.5 }}>
              Découvrez notre sélection soigneusement choisie pour vous.
            </p>
            <div style={{ display:'flex', gap:'12px', flexWrap:'wrap' }}>
              <button
                onClick={() => document.getElementById('vr-products')?.scrollIntoView({ behavior:'smooth' })}
                style={{
                  padding:'12px 26px', borderRadius:'24px', border:'none', cursor:'pointer',
                  background:DS.gold, color:DS.white,
                  fontFamily:'DM Sans,sans-serif', fontSize:'13px', fontWeight:500, letterSpacing:'0.5px',
                  transition:'opacity 0.2s',
                }}
              >
                Voir la collection
              </button>
              {contactInfo?.telephone && (
                <a href={`tel:${contactInfo.telephone}`}
                  style={{
                    padding:'12px 26px', borderRadius:'24px', cursor:'pointer',
                    background:'transparent', color:DS.white,
                    border:'1.5px solid rgba(255,255,255,0.2)', textDecoration:'none',
                    fontFamily:'DM Sans,sans-serif', fontSize:'13px', fontWeight:400, letterSpacing:'0.5px',
                    display:'inline-flex', alignItems:'center', gap:'6px',
                  }}>
                  <Phone style={{ width:'14px', height:'14px' }} />
                  Nous contacter
                </a>
              )}
            </div>
          </div>

          {/* Right — compact circle badge (80px max) */}
          <div className="vr-hero-badge" style={{
            width:'80px', height:'80px', borderRadius:'50%', flexShrink:0,
            border:`1.5px solid ${DS.gold}`,
            display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center',
          }}>
            <span className="vr-serif vr-badge-num" style={{ fontSize:'32px', fontWeight:300, color:DS.white, lineHeight:1 }}>
              {products.length}
            </span>
            <span style={{ fontFamily:'DM Sans,sans-serif', fontSize:'8px', color:DS.stone,
              letterSpacing:'2px', textTransform:'uppercase', marginTop:'2px' }}>
              articles
            </span>
          </div>
        </div>
      </section>

      {/* 3. FILTER BAR ───────────────────────────────────────────────────── */}
      <div style={{ background:DS.white, borderBottom:'1px solid rgba(26,23,20,0.08)', position:'sticky', top:'57px', zIndex:40 }}>
        <div className="vr-filter-inner" style={{ maxWidth:'1200px', margin:'0 auto', padding:'0.875rem 2rem', display:'flex', alignItems:'center', gap:'14px' }}>
          {/* Search */}
          <div className="vr-filter-search" style={{ position:'relative', flexShrink:0, width:'220px' }}>
            <Search style={{ position:'absolute', left:'13px', top:'50%', transform:'translateY(-50%)', width:'15px', height:'15px', color:DS.stone }} />
            <input type="text" placeholder="Rechercher..." value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              style={{
                width:'100%', paddingLeft:'38px', paddingRight:'14px', paddingTop:'9px', paddingBottom:'9px',
                background:DS.cream, border:'1px solid rgba(26,23,20,0.08)', borderRadius:'24px',
                fontFamily:'DM Sans,sans-serif', fontSize:'13px', color:DS.ink, outline:'none',
              }}
              data-testid="vitrine-search"
            />
          </div>

          {/* Sort + Clear row (on mobile sits above chips) */}
          <div className="vr-filter-top-row" style={{ display:'contents' }}>
            {/* Sort dropdown */}
            <div className="vr-filter-sort" style={{ position:'relative', flexShrink:0, marginLeft:'auto' }}>
              <select value={sortOrder} onChange={e => setSortOrder(e.target.value)}
                style={{
                  padding:'8px 32px 8px 14px', borderRadius:'12px', appearance:'none',
                  background:DS.white, border:'1px solid rgba(26,23,20,0.12)',
                  fontFamily:'DM Sans,sans-serif', fontSize:'12px', color:DS.ink,
                  cursor:'pointer', outline:'none',
                }}
                data-testid="sort-select"
              >
                <option value="default">Trier par</option>
                <option value="price_asc">Prix croissant</option>
                <option value="price_desc">Prix décroissant</option>
                <option value="name">Alphabétique</option>
              </select>
              <ChevronDown style={{ position:'absolute', right:'10px', top:'50%', transform:'translateY(-50%)',
                width:'13px', height:'13px', color:DS.stone, pointerEvents:'none' }} />
            </div>

            {/* Clear filters */}
            {hasActiveFilters && (
              <button onClick={clearFilters}
                style={{ padding:'7px 12px', borderRadius:'12px', background:'transparent', border:'1px solid rgba(26,23,20,0.12)',
                  cursor:'pointer', display:'flex', alignItems:'center', gap:'5px', flexShrink:0,
                  fontFamily:'DM Sans,sans-serif', fontSize:'12px', color:'#6B6560' }}>
                <X style={{ width:'13px', height:'13px' }} />
                Effacer
              </button>
            )}
          </div>

          {/* Category chips */}
          <div className="vr-filter-chips" style={{ display:'flex', gap:'7px', flex:1, overflowX:'auto', alignItems:'center', flexWrap:'wrap' }}>
            {[{ id:'', name:'Tous' }, ...categories].map(cat => {
              const active = selectedCategory === cat.id;
              return (
                <button key={cat.id} onClick={() => setSelectedCategory(active && cat.id ? '' : cat.id)}
                  style={{
                    padding:'7px 16px', borderRadius:'20px', whiteSpace:'nowrap', flexShrink:0, cursor:'pointer',
                    background: active ? DS.ink : 'transparent',
                    color: active ? DS.white : '#6B6560',
                    border: `1px solid ${active ? DS.ink : 'rgba(26,23,20,0.12)'}`,
                    fontFamily:'DM Sans,sans-serif', fontSize:'13px', fontWeight:500, transition:'all 0.2s',
                  }}
                  data-testid={`filter-chip-${cat.name}`}
                >
                  {cat.name}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* 4. SECTION HEADER ───────────────────────────────────────────────── */}
      <div id="vr-products" className="vr-section-hdr" style={{ maxWidth:'1200px', margin:'0 auto', padding:'2rem 2rem 1.25rem', display:'flex', alignItems:'baseline', justifyContent:'space-between' }}>
        <h2 className="vr-serif" style={{ fontSize:'22px', fontWeight:400, color:DS.ink }}>
          Articles disponibles
        </h2>
        <span style={{ fontFamily:'DM Sans,sans-serif', fontSize:'13px', color:DS.stone }}>
          {sortedProducts.length} article{sortedProducts.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* 5. PRODUCT GRID ─────────────────────────────────────────────────── */}
      <main className="vr-main" style={{ maxWidth:'1200px', margin:'0 auto', padding:'0 2rem 3.5rem' }}>
        {sortedProducts.length === 0 ? (
          /* Elegant empty state */
          <div style={{ textAlign:'center', padding:'6rem 2rem', display:'flex', flexDirection:'column', alignItems:'center', gap:'16px' }}>
            <div style={{ width:'48px', height:'1px', background:DS.stone }} />
            <p className="vr-serif" style={{ fontSize:'28px', fontStyle:'italic', fontWeight:300, color:DS.muted }}>
              Aucun article pour le moment
            </p>
            <div style={{ width:'48px', height:'1px', background:DS.stone }} />
            {hasActiveFilters && (
              <button onClick={clearFilters}
                style={{ marginTop:'12px', padding:'10px 22px', borderRadius:'24px', border:`1.5px solid ${DS.gold}`,
                  background:'transparent', color:DS.gold, cursor:'pointer',
                  fontFamily:'DM Sans,sans-serif', fontSize:'13px' }}>
                Effacer les filtres
              </button>
            )}
          </div>
        ) : (
          <div className="vr-grid" style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:'14px' }}>
            {sortedProducts.map((product, index) => (
              <LuxeProductCard
                key={product.id}
                product={product}
                onClick={() => openProductDetail(product)}
                isFavorite={customerFavorites.has(product.id)}
                onToggleFavorite={toggleFavorite}
                devise={devise}
                animDelay={index * 0.06}
              />
            ))}
          </div>
        )}
      </main>

      {/* 7. REVIEWS SECTION — Masqué temporairement
      {reviews.length > 0 && (
        <ReviewsSection reviews={reviews} averageRating={averageRating} storeName={boutiqueName} />
      )}
      */}

      {/* 8. FOOTER ───────────────────────────────────────────────────────── */}
      <footer style={{ background:DS.ink }}>
        <div className="vr-footer-inner" style={{ maxWidth:'1200px', margin:'0 auto', padding:'20px 32px', display:'flex', alignItems:'center', justifyContent:'space-between', gap:'1rem' }}>
          <p style={{ fontFamily:'DM Sans,sans-serif', fontSize:'12px', color:'#7A736B' }}>
            © {new Date().getFullYear()} {boutiqueName}. Tous droits réservés.
          </p>
          {contactInfo?.telephone && (
            <a href={`tel:${contactInfo.telephone}`}
              style={{ display:'flex', alignItems:'center', gap:'6px', textDecoration:'none',
                fontFamily:'DM Sans,sans-serif', fontSize:'12px', color:DS.gold }}>
              <Phone style={{ width:'13px', height:'13px', color:DS.gold }} />
              {contactInfo.telephone}
            </a>
          )}
        </div>
      </footer>

      {/* ── PRODUCT DETAIL MODAL (logic unchanged, design updated) ── */}
      {/* ── PRODUCT DETAIL MODAL (custom overlay) ── */}
      {productDetailOpen && selectedProduct && (
        <div
          onClick={() => setProductDetailOpen(false)}
          style={{ position:'fixed', inset:0, background:'rgba(26,23,20,0.65)', zIndex:9999,
            display:'flex', alignItems:'center', justifyContent:'center', backdropFilter:'blur(4px)', WebkitBackdropFilter:'blur(4px)' }}
          data-testid="product-detail-overlay"
        >
          <div
            onClick={e => e.stopPropagation()}
            className="vr-detail-modal"
            style={{ background:DS.cream, borderRadius:'16px', maxWidth:'720px', width:'90%', maxHeight:'85vh',
              overflowY:'auto', display:'grid', gridTemplateColumns:'1fr 1fr', gap:0 }}
            data-testid="product-detail-modal"
          >
            {/* Left — image */}
            <div className="vr-detail-img" style={{ position:'relative', minHeight:'360px', background:DS.sand,
              borderRadius:'16px 0 0 16px', overflow:'hidden' }}>
              {(() => {
                const src = getImageSrc(selectedProduct);
                if (src) return <img src={src} alt={selectedProduct.name}
                  style={{ width:'100%', height:'100%', objectFit:'cover', objectPosition:'top center', position:'absolute', inset:0 }}
                  onError={e => { e.target.style.display='none'; e.target.nextSibling.style.display='flex'; }} />;
                return null;
              })()}
              <div style={{ display: getImageSrc(selectedProduct) ? 'none' : 'flex', width:'100%', height:'100%', position:'absolute', inset:0 }}>
                <ShirtPlaceholder />
              </div>
            </div>

            {/* Right — product info */}
            <div className="vr-detail-info" style={{ padding:'32px 28px', position:'relative', display:'flex', flexDirection:'column', gap:'16px' }}>
              {/* Close button */}
              <button
                onClick={() => setProductDetailOpen(false)}
                style={{ position:'absolute', top:'16px', right:'16px', width:'24px', height:'24px', borderRadius:'50%',
                  background:'rgba(26,23,20,0.08)', border:'none', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center',
                  fontFamily:'DM Sans,sans-serif', fontSize:'14px', color:DS.ink, lineHeight:1 }}
                data-testid="product-detail-close"
              >
                ×
              </button>

              {/* Category */}
              <p style={{ fontFamily:'DM Sans,sans-serif', fontSize:'10px', color:'#9C8880', textTransform:'uppercase', letterSpacing:'1.5px' }}>
                {selectedProduct.category_name}
              </p>

              {/* Name */}
              <h2 className="vr-serif" style={{ fontSize:'28px', fontWeight:400, color:DS.ink, lineHeight:1.2, marginTop:'-8px' }}>
                {selectedProduct.name}
              </h2>

              {/* Price */}
              <p style={{ fontFamily:'DM Sans,sans-serif', fontSize:'20px', fontWeight:700, color:DS.ink }}>
                {selectedProduct.sale_price.toLocaleString('fr-FR')} {devise}
              </p>

              {/* Divider */}
              <div style={{ height:'1px', background:'rgba(26,23,20,0.08)' }} />

              {!selectedProduct.is_out_of_stock ? (
                <>
                  {/* Sizes */}
                  {selectedProduct.available_sizes?.length > 0 && (
                    <div>
                      <p style={{ fontFamily:'DM Sans,sans-serif', fontSize:'11px', color:'#9C8880', textTransform:'uppercase', letterSpacing:'1.5px', marginBottom:'10px' }}>
                        Taille
                      </p>
                      <div style={{ display:'flex', flexWrap:'wrap', gap:'8px' }}>
                        {selectedProduct.available_sizes.map(size => {
                          const active = selectedVariant.size === size;
                          return (
                            <button key={size} onClick={() => setSelectedVariant(p => ({ ...p, size }))}
                              style={{ width:'36px', height:'36px', borderRadius:'8px', cursor:'pointer',
                                border: `1px solid ${active ? DS.ink : 'rgba(26,23,20,0.15)'}`,
                                background: active ? DS.ink : 'transparent', color: active ? DS.white : DS.ink,
                                fontFamily:'DM Sans,sans-serif', fontSize:'12px', display:'flex', alignItems:'center', justifyContent:'center',
                                transition:'all 0.2s' }}>
                              {size}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Colors */}
                  {selectedProduct.available_colors?.length > 0 && (
                    <div>
                      <p style={{ fontFamily:'DM Sans,sans-serif', fontSize:'11px', color:'#9C8880', textTransform:'uppercase', letterSpacing:'1.5px', marginBottom:'10px' }}>
                        Couleur
                      </p>
                      <div style={{ display:'flex', flexWrap:'wrap', gap:'8px', alignItems:'center' }}>
                        {selectedProduct.available_colors.map(color => {
                          const active = selectedVariant.color === color.name;
                          return (
                            <button key={color.name} onClick={() => setSelectedVariant(p => ({ ...p, color: color.name }))}
                              title={color.name}
                              style={{ width:'14px', height:'14px', borderRadius:'50%', cursor:'pointer', border:'1px solid rgba(26,23,20,0.15)',
                                background: color.hex || DS.border, flexShrink:0, padding:0,
                                outline: active ? `2px solid ${DS.gold}` : 'none', outlineOffset: active ? '2px' : '0',
                                transition:'outline 0.15s' }}>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Stock info */}
                  <p style={{ fontFamily:'DM Sans,sans-serif', fontSize:'12px', color:'#9C8880' }}>
                    {selectedVariant.size && selectedVariant.color
                      ? (getSelectedVariantStock() > 0
                          ? `Stock : ${getSelectedVariantStock()} disponible(s)`
                          : 'Indisponible dans cette combinaison')
                      : `Stock total : ${selectedProduct.total_quantity || 0} disponible(s)`
                    }
                  </p>

                  {/* Quantity selector */}
                  {selectedVariant.size && selectedVariant.color && getSelectedVariantStock() > 0 && (
                    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'10px 14px', background:DS.sand, borderRadius:'10px' }}>
                      <span style={{ fontFamily:'DM Sans,sans-serif', fontSize:'13px', fontWeight:500, color:DS.ink }}>Quantité</span>
                      <div style={{ display:'flex', alignItems:'center', gap:'12px' }}>
                        <button type="button" onClick={() => setInterestForm(p => ({ ...p, quantity: Math.max(1, (p.quantity||1)-1) }))}
                          style={{ width:'28px', height:'28px', borderRadius:'50%', background:DS.white, border:`1px solid ${DS.border}`,
                            cursor:'pointer', fontSize:'15px', display:'flex', alignItems:'center', justifyContent:'center', color:DS.ink }}>
                          −
                        </button>
                        <span style={{ fontFamily:'DM Sans,sans-serif', fontSize:'14px', fontWeight:600, minWidth:'18px', textAlign:'center', color:DS.ink }}>
                          {interestForm.quantity || 1}
                        </span>
                        <button type="button" onClick={() => setInterestForm(p => ({ ...p, quantity: Math.min(getSelectedVariantStock()||10, (p.quantity||1)+1) }))}
                          style={{ width:'28px', height:'28px', borderRadius:'50%', background:DS.white, border:`1px solid ${DS.border}`,
                            cursor:'pointer', fontSize:'15px', display:'flex', alignItems:'center', justifyContent:'center', color:DS.ink }}>
                          +
                        </button>
                      </div>
                    </div>
                  )}

                  {/* [CONV-1-SYSTEME] Bouton "Je suis intéressée" */}
                  <button
                    onClick={handleInteresse}
                    disabled={interestLoading}
                    data-testid="interesse-btn"
                    style={{
                      width:'100%', background: accentColor, color:'#fff', borderRadius:'10px', padding:'13px',
                      fontFamily:'DM Sans,sans-serif', fontSize:'14px', fontWeight:600, border:'none', cursor:'pointer',
                      opacity: interestLoading ? 0.6 : 1, transition:'opacity 0.2s',
                      display:'flex', alignItems:'center', justifyContent:'center', gap:'8px'
                    }}
                  >
                    <Heart style={{ width:'16px', height:'16px' }} />
                    {interestLoading ? "Envoi..." : "Je suis intéressée"}
                  </button>
                </>
              ) : (
                <p style={{ fontFamily:'DM Sans,sans-serif', fontSize:'13px', color:'#EF4444', fontWeight:500, textAlign:'center', padding:'8px 0' }}>
                  Rupture de stock
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── INTEREST / ORDER FORM MODAL (logic unchanged) ── */}
      <Dialog open={interestFormOpen} onOpenChange={(open) => { setInterestFormOpen(open); if (!open) setOrderSuccess(null); }}>
        <DialogContent style={{ background:DS.white, border:`1px solid ${DS.border}`, maxWidth:'420px', borderRadius:'16px' }} data-testid="interest-form-modal">
          {orderSuccess ? (
            <div style={{ textAlign:'center', padding:'1.5rem 0' }}>
              <div style={{ width:'60px', height:'60px', borderRadius:'50%', background:'#f0fdf4', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 1.25rem' }}>
                <CheckCircle style={{ width:'30px', height:'30px', color:'#22863a' }} />
              </div>
              <h2 className="vr-serif" style={{ fontSize:'24px', fontWeight:400, color:DS.ink, marginBottom:'8px' }}>Commande envoyée !</h2>
              <p style={{ fontFamily:'DM Sans,sans-serif', fontSize:'13px', color:DS.muted, marginBottom:'1.25rem' }}>
                Votre commande <strong>{orderSuccess.orderNumber}</strong> a été transmise à la boutique.
              </p>
              <div style={{ background:DS.sand, borderRadius:'12px', padding:'16px', marginBottom:'12px' }}>
                <p style={{ fontFamily:'DM Sans,sans-serif', fontSize:'12px', color:DS.muted, marginBottom:'10px' }}>
                  Suivez votre commande et échangez avec la boutique :
                </p>
                <Button onClick={() => window.open(orderSuccess.accessUrl, '_blank')}
                  style={{ width:'100%', background:DS.ink, color:DS.white, borderRadius:'24px', fontFamily:'DM Sans,sans-serif', marginBottom:'8px' }}
                  data-testid="track-order-btn">
                  <MessageCircle style={{ width:'15px', height:'15px', marginRight:'6px' }} />
                  Suivre ma commande dans l'app
                </Button>
                {contactInfo?.telephone && (
                  <Button
                    onClick={() => {
                      const items = selectedProduct ? `• ${selectedProduct.name} (${selectedVariant.size} / ${selectedVariant.color}) x${interestForm.quantity || 1} — ${((selectedProduct.sale_price || 0) * (interestForm.quantity || 1)).toFixed(2)} ${devise}` : '';
                      const msg = `Bonjour ${boutiqueName} !\nJe suis ${interestForm.customer_name || clienteSession?.name || ''}, j'aimerais commander :\n\n${items}\n\nCommande n° ${orderSuccess.orderNumber}\nMon contact : ${interestForm.customer_phone || clienteSession?.phone || ''}`;
                      const tel = contactInfo.telephone.replace(/\s/g, '');
                      window.open(`https://wa.me/${tel}?text=${encodeURIComponent(msg)}`, '_blank');
                    }}
                    style={{ width:'100%', background:'#25D366', color:DS.white, borderRadius:'24px', fontFamily:'DM Sans,sans-serif' }}
                    data-testid="whatsapp-order-btn"
                  >
                    <svg style={{ width:'15px', height:'15px', marginRight:'6px', flexShrink:0 }} viewBox="0 0 24 24" fill="currentColor">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                    </svg>
                    Contacter via WhatsApp
                  </Button>
                )}
              </div>
              <Button variant="outline" onClick={() => { setInterestFormOpen(false); setProductDetailOpen(false); setOrderSuccess(null); }}
                style={{ width:'100%', border:`1px solid ${DS.border}`, borderRadius:'24px', fontFamily:'DM Sans,sans-serif' }}>
                Continuer mes achats
              </Button>
            </div>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle className="vr-serif" style={{ fontSize:'22px', fontWeight:400, color:DS.ink }}>Commander cet article</DialogTitle>
                <DialogDescription style={{ fontFamily:'DM Sans,sans-serif', fontSize:'12px', color:DS.stone }}>
                  {selectedProduct?.name} — {selectedVariant.size} / {selectedVariant.color}
                </DialogDescription>
              </DialogHeader>

              <form onSubmit={submitInterest} style={{ marginTop:'1rem', display:'flex', flexDirection:'column', gap:'1rem' }}>
                {/* Qty */}
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'12px 16px', background:DS.sand, borderRadius:'12px' }}>
                  <span style={{ fontFamily:'DM Sans,sans-serif', fontSize:'13px', fontWeight:500 }}>Quantité</span>
                  <div style={{ display:'flex', alignItems:'center', gap:'14px' }}>
                    <button type="button" onClick={() => setInterestForm(p => ({ ...p, quantity:Math.max(1,(p.quantity||1)-1) }))}
                      style={{ width:'30px', height:'30px', borderRadius:'50%', background:DS.white, border:`1px solid ${DS.border}`, cursor:'pointer', fontSize:'16px', display:'flex', alignItems:'center', justifyContent:'center' }}>
                      −
                    </button>
                    <span style={{ fontFamily:'DM Sans,sans-serif', fontSize:'14px', fontWeight:500, minWidth:'20px', textAlign:'center' }}>{interestForm.quantity||1}</span>
                    <button type="button" onClick={() => setInterestForm(p => ({ ...p, quantity:Math.min(getSelectedVariantStock()||10,(p.quantity||1)+1) }))}
                      style={{ width:'30px', height:'30px', borderRadius:'50%', background:DS.white, border:`1px solid ${DS.border}`, cursor:'pointer', fontSize:'16px', display:'flex', alignItems:'center', justifyContent:'center' }}>
                      +
                    </button>
                  </div>
                </div>

                {/* Total */}
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'12px 16px', background:`${DS.gold}10`, borderRadius:'12px' }}>
                  <span style={{ fontFamily:'DM Sans,sans-serif', fontSize:'13px', fontWeight:500 }}>Total estimé</span>
                  <span className="vr-serif" style={{ fontSize:'22px', fontWeight:400, color:DS.ink }}>
                    {((selectedProduct?.sale_price||0)*(interestForm.quantity||1)).toFixed(2)} {devise}
                  </span>
                </div>

                {[
                  { label:'Votre prénom *', field:'customer_name', type:'text', placeholder:'Marie', testid:'interest-name', required:true },
                  { label:'WhatsApp / Téléphone *', field:'customer_phone', type:'tel', placeholder:'+33 6 12 34 56 78', testid:'interest-phone', required:true },
                  { label:'Email (optionnel)', field:'customer_email', type:'email', placeholder:'marie@email.com', testid:'interest-email', required:false },
                ].map(f => (
                  <div key={f.field}>
                    <Label style={{ fontFamily:'DM Sans,sans-serif', fontSize:'12px', color:DS.ink, fontWeight:500, marginBottom:'6px', display:'block' }}>
                      {f.label}
                      {clienteSession && (f.field === 'customer_name' || f.field === 'customer_phone') && (
                        <span style={{ color:DS.gold, fontSize:'11px', marginLeft:'6px', fontWeight:400 }}>✓ pré-rempli</span>
                      )}
                    </Label>
                    <Input type={f.type} value={interestForm[f.field]} required={f.required} placeholder={f.placeholder}
                      onChange={e => setInterestForm(p => ({ ...p, [f.field]:e.target.value }))}
                      style={{ background:DS.sand, border:`1px solid ${DS.border}`, borderRadius:'12px', fontFamily:'DM Sans,sans-serif', fontSize:'13px' }}
                      data-testid={f.testid} />
                  </div>
                ))}

                <div>
                  <Label style={{ fontFamily:'DM Sans,sans-serif', fontSize:'12px', color:DS.ink, fontWeight:500, marginBottom:'6px', display:'block' }}>Message (optionnel)</Label>
                  <Textarea value={interestForm.message} placeholder="Question, demande particulière..."
                    onChange={e => setInterestForm(p => ({ ...p, message:e.target.value }))}
                    style={{ background:DS.sand, border:`1px solid ${DS.border}`, borderRadius:'12px', fontFamily:'DM Sans,sans-serif', fontSize:'13px', minHeight:'72px' }}
                    data-testid="interest-message" />
                </div>

                <DialogFooter style={{ gap:'8px', paddingTop:'4px' }}>
                  <Button type="button" variant="outline" onClick={() => setInterestFormOpen(false)}
                    style={{ border:`1px solid ${DS.border}`, borderRadius:'24px', fontFamily:'DM Sans,sans-serif' }}>
                    Annuler
                  </Button>
                  <Button type="submit" disabled={submitting}
                    style={{ background:DS.ink, color:DS.white, borderRadius:'24px', fontFamily:'DM Sans,sans-serif' }}
                    data-testid="submit-interest-btn">
                    {submitting ? <><Loader2 style={{ width:'15px', height:'15px', marginRight:'6px', animation:'spin 1s linear infinite' }} />Envoi...</> : <><ShoppingBag style={{ width:'15px', height:'15px', marginRight:'6px' }} />Envoyer ma commande</>}
                  </Button>
                </DialogFooter>
              </form>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* ── AUTH MODAL (inline for Commander flow) ── */}
      <InlineAuthModal
        open={authModalOpen}
        onClose={() => { setAuthModalOpen(false); setPendingCartProduct(null); }}
        onAuth={handleAuthSuccess}
        storeId={userId}
        storeName={boutiqueName}
        accentColor={accentColor}
      />

      {/* ── FLOATING CONTACT BUTTON ── */}
      <button
        onClick={() => {
          if (customerToken) {
            navigate(`/vitrine/${userId}/espace/messages`);
          } else {
            setPendingCartProduct(null);
            setAuthModalOpen(true);
          }
        }}
        data-testid="contact-fab"
        style={{
          position:'fixed', bottom:'24px', right:'24px', zIndex:9000,
          width:'52px', height:'52px', borderRadius:'50%',
          background: accentColor || DS.gold, color:'#fff', border:'none', cursor:'pointer',
          boxShadow:'0 4px 20px rgba(0,0,0,0.2)',
          display:'flex', alignItems:'center', justifyContent:'center',
          transition:'transform 0.2s',
        }}
        onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.1)'}
        onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
        title="Nous contacter"
      >
        <MessageCircle style={{ width:'22px', height:'22px' }} />
      </button>
    </div>
  );
}
